import { useNavigate, useSearch } from "@tanstack/solid-router";
import { createInfiniteQuery, createQuery, keepPreviousData } from "@tanstack/solid-query";
import { For, Show, createMemo, createSignal } from "solid-js";
import { CompanyCard } from "~/components/CompanyCard";
import { IntelEmptyState } from "~/components/IntelEmptyState";
import { TicketClaimGate } from "~/components/TicketClaimGate";
import { everClaimedTicket, fetchCompanies, fetchProfile, type CompanyCard as CompanyCardData } from "~/lib/api";
import { createDebouncedValue } from "~/lib/debounce";
import { profileQueryKey } from "~/lib/routes";

const SECTOR_FILTERS = ["Vendor", "Employer", "Consultancy", "Education"] as const;

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
      <path d="M20 20L16 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
    </svg>
  );
}

export function CompaniesPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { q?: string };
  const [q, setQ] = createSignal(search.q ?? "");
  const [sector, setSector] = createSignal("");
  const [hiring, setHiring] = createSignal(false);
  const debouncedQ = createDebouncedValue(q, 350);

  const profileQuery = createQuery(() => ({
    queryKey: profileQueryKey(),
    queryFn: () => fetchProfile(),
  }));
  const needsTicket = () => profileQuery.isSuccess && !everClaimedTicket(profileQuery.data);

  const filters = createMemo(() => ({
    q: debouncedQ().trim(),
    sector: sector() || undefined,
    hiring: hiring() || undefined,
  }));

  const listQuery = createInfiniteQuery(() => ({
    queryKey: ["companies", filters()],
    queryFn: ({ pageParam }) =>
      fetchCompanies({
        ...filters(),
        cursor: pageParam as string | undefined,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor || undefined,
    placeholderData: keepPreviousData,
    enabled: everClaimedTicket(profileQuery.data),
  }));

  const items = createMemo(() => (listQuery.data?.pages ?? []).flatMap((p) => p.data));

  function openCompany(card: CompanyCardData) {
    navigate({ to: "/companies/$slug", params: { slug: card.slug } });
  }

  return (
    <section class="page page--flush">
      <Show when={needsTicket()}>
        <TicketClaimGate
          cityLabel="Company intel"
          heading="Link your conference ticket"
          lede="Company intel is a Connect Pass benefit. Meetup-only members do not get this surface."
        />
      </Show>

      <Show when={!needsTicket()}>
        <div class="page-title-block">
          <p class="status-chip">Ticket holder</p>
          <h1>Companies</h1>
        </div>

        <div class="filter-chips">
          <For each={SECTOR_FILTERS}>
            {(chip) => (
              <button
                type="button"
                class={`interest-chip${sector() === chip ? " selected" : ""}`}
                onClick={() => setSector((cur) => (cur === chip ? "" : chip))}
              >
                {chip}
              </button>
            )}
          </For>
          <button
            type="button"
            class={`interest-chip${hiring() ? " selected" : ""}`}
            onClick={() => setHiring((v) => !v)}
          >
            Hiring
          </button>
        </div>

        <label class="search-bar">
          <SearchIcon />
          <input
            type="search"
            enterkeyhint="search"
            autocomplete="off"
            placeholder="Search companies…"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
          />
        </label>

        <Show when={listQuery.isPending && !listQuery.data}>
          <p class="muted" style={{ padding: "0 20px" }}>
            Loading companies…
          </p>
        </Show>
        <Show when={listQuery.isError}>
          <div class="error-box" style={{ margin: "0 20px" }}>
            {(listQuery.error as Error).message}
          </div>
        </Show>
        <Show when={!listQuery.isPending && items().length === 0}>
          <IntelEmptyState
            title="No companies in the index yet"
            body="Intel is a Connect Pass benefit — check back after the next ingest."
            action={
              <a class="btn btn-primary" href="/directory">
                Open the directory
              </a>
            }
          />
        </Show>

        <ul class="directory-grid">
          <For each={items()}>
            {(card, i) => (
              <li>
                <CompanyCard card={card} featured={i() === 0} onOpen={openCompany} />
              </li>
            )}
          </For>
        </ul>

        <Show when={listQuery.hasNextPage}>
          <div class="wizard-actions" style={{ padding: "0 20px 16px" }}>
            <button
              type="button"
              class="btn btn-outline"
              disabled={listQuery.isFetchingNextPage}
              onClick={() => listQuery.fetchNextPage()}
            >
              {listQuery.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        </Show>
        <Show when={items().length > 0}>
          <p class="muted" style={{ padding: "0 20px 20px" }}>
            Looking for people? <a href="/directory">Open the directory</a>.
          </p>
        </Show>
      </Show>
    </section>
  );
}
